import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import type { KeyboardEventHandler } from 'react';
import { Box, Typography, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import { Formik } from 'formik';
import type { FormikProps } from 'formik';
import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/pages/warehouses/WarehouseSelector';
import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import CustomButton from 'src/core/ui/CustomButton';
import ContentCardsContainer, { CardType, ContentCard } from 'src/core/ui/ContentCard';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import { generateTablePageButtons } from 'src/core/tables/generateTablePageButtons';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { ScannerButton, ScannerDialog, useScannerConfiguration } from 'src/shared/scanning/Scanner';
import { useReceivingOrdersTable, receivingAdviceTypes, useReceivingOrderMatchesTable } from './useReceivingOrdersTable';
import type { ReceivingOrderRow, ReceivingOrdersTableData } from './useReceivingOrdersTable';
import ScannerInput from 'src/shared/scanning/ScannerInput';
import * as Yup from 'yup';
import type { tableSettingsProps } from 'src/core/tables/useTableSettings';
import useScanner from 'src/shared/scanning/useScanner';
import type { ScannerEvent } from 'src/shared/scanning/useScanner';
import { findScanMatches } from 'src/shared/scanning/scanClassifier';
import { useReceiveProducts } from 'src/pages/warehouses/warehouseRequests';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { useDispatchPrintingSettings, ProductLabelPrintJob } from 'src/shared/printing/PrintingManager';
import type { DispatchPrintingSettings, ProductLabelPrintResult } from 'src/shared/printing/PrintingManager';
import WarehouseRowEditor from 'src/pages/warehouses/WarehouseRowEditor';
import { alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import PrintIcon from '@mui/icons-material/Print';
import { FormField } from 'src/core/forms/FormField';
import { CustomField } from 'src/core/forms/CustomField';
import { useUpdateProduct } from 'src/pages/products/productRequests';
import { printProductLabel } from 'src/core/api/apiRequests';

export const ReceivingOrdersTablePage = () => {
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    // Accepted follow-up jobs outlive the warehouse-keyed table/editor below.
    const followUps = useReceivingFollowUps();
    const submitRef = useRef<(() => boolean) | null>(null);
    return <>
        <WarehouseSelectionPopup />
        <Formik key={warehouseId ?? 'no-warehouse'} initialValues={{ Condition: 2 } as ReceiveValues} validationSchema={receiveSchema} onSubmit={(_, helpers) => { if (!submitRef.current?.()) helpers.setSubmitting(false); }}>{formik => <ReceivingOrdersContent warehouseId={warehouseId} onWarehouseChange={selectWarehouse} followUps={followUps} formik={formik} submitRef={submitRef} />}</Formik>
        {followUps.jobs}
    </>;
};

const ReceivingOrdersContent = ({ warehouseId, onWarehouseChange, followUps, formik, submitRef }: { warehouseId: Identifier | null; onWarehouseChange: (value: Identifier) => void; followUps: ReturnType<typeof useReceivingFollowUps>; formik: FormikProps<ReceiveValues>; submitRef: { current: (() => boolean) | null } }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const selectRowRef = useRef<((row: ReceivingOrderRow) => void) | null>(null);
    const selectRow = useCallback((row: ReceivingOrderRow) => selectRowRef.current?.(row), []);
    const [columns, tableSettings, data] = useReceivingOrdersTable(warehouseId, refreshKey, selectRow);
    const { settings, setSetting } = useSettingsContext();
    const isReceivingFormVisible = settings?.receivingOrdersFormVisible ?? true;
    const onReceivingFormVisibilityChange = useCallback((visible: boolean) => setSetting('receivingOrdersFormVisible', visible), [setSetting]);
    const onReceived = useCallback(() => setRefreshKey(current => current + 1), []);
    const scannerConfiguration = useScannerConfiguration();

    const station = useReceivingStation({ warehouseId, onWarehouseChange, tableData: data, tableSettings, onReceived, isReceivingFormVisible, onReceivingFormVisibilityChange, onPrintProductLabel: followUps.enqueueProductLabel, onSaveProductBarcode: followUps.enqueueProductBarcode, formik });
    selectRowRef.current = station.selectRow;
    submitRef.current = station.submitReceive;
    return (
        <Box onKeyDown={station.onKeyDown}>
            <PageHeader {...{
                title: 'Receiving Orders',
                subTitle: 'Receive Products from Suppliers etc, and add them to your Warehouse Inventory',
                components: [
                    { isInGroupedMenu: true, component: <ScannerButton variant="outlined" onClick={scannerConfiguration.openDialog} /> },
                    ...(warehouseId != null ? [{ position: 'center', component: <ScannerInput scanner={station.scanner} /> }] : []),
                    ...(warehouseId == null ? [] : generateTablePageButtons(tableSettings)),
                    { component: <WarehouseSelector value={warehouseId} onChange={station.changeWarehouse} /> },
                    ...(isReceivingFormVisible && warehouseId != null ? [] : [{ component: <ReceivingAction station={station} /> }]),
                    { isInGroupedMenu: true, component: <CustomButton variant="text" title={isReceivingFormVisible ? 'Hide receiving form' : 'Show receiving form'} onClick={() => onReceivingFormVisibilityChange(!isReceivingFormVisible)} /> },
                ],
            }} />
            <ScannerDialog {...scannerConfiguration.dialogProps} />
            {followUps.feedback}
            <ContentCardsContainer>
                {warehouseId == null ? (
                    <ContentCard cardId="ReceiveProducts" md={12} cardTypes={[CardType.noHeader]}>
                        <Typography>Select a warehouse to scan and receive products.</Typography>
                    </ContentCard>
                ) : isReceivingFormVisible && (
                    <ReceivingEditor station={station} warehouseId={warehouseId} />
                )}
                {warehouseId != null && <ContentCard cardId="ReceivingOrders" md={12} cardTypes={[CardType.noHeader]} contentPadding={0}>
                    <CustomDataGrid columns={columns} tableSettings={tableSettings} />
                </ContentCard>}
            </ContentCardsContainer>
            <ReceivingDialogs station={station} status={data.status} />
            <Footer />
        </Box>
    );
};

export default ReceivingOrdersTablePage;

export { default as WarehouseFilter } from 'src/pages/warehouses/WarehouseSelector';

export const useReceivingStation = ({ warehouseId, onWarehouseChange, isReceivingFormVisible, onReceivingFormVisibilityChange, onPrintProductLabel, onSaveProductBarcode, tableData: { rows }, tableSettings, onReceived, formik }: ReceivingStationOptions) => {
    const { settings, setSetting } = useSettingsContext();
    const printingSettings = useDispatchPrintingSettings();
    const printProductLabel = settings?.receivingOrdersPrintProductLabel === true || settings?.receivingOrdersPrintProductLabel === 'true';
    const printProductLabelRef = useRef(printProductLabel);
    printProductLabelRef.current = printProductLabel;
    const pendingProductLabelRef = useRef<ProductLabelRequest | null>(null);
    const { updateMessageStatus } = useMessageContext();
    const reportReceiveError = useCallback((message: string) => updateMessageStatus({ requestUrl: '/api/warehouses/receiving', status: 'error', message }), [updateMessageStatus]);
    const [barcodeValue, setBarcodeValue] = useState('');
    const barcodeValueRef = useRef('');
    const pendingProductBarcodeRef = useRef<ProductBarcodeRequest | null>(null);
    const updateBarcodeValue = useCallback((value: string) => { barcodeValueRef.current = value; setBarcodeValue(value); }, []);
    const [labelDialog, setLabelDialog] = useState<{ productId: Identifier; copies: string } | null>(null);
    const reprintSubmittedRef = useRef(false);
    const [receivePayload, setReceivePayload] = useState<ReceivePayload | null>(null);
    const receiveResult = useReceiveProducts(receivePayload);
    const receiveRowAtSubmitRef = useRef<unknown>(null);
    const receiveStatusAtSubmitRef = useRef(receiveResult?.status);
    const receiveObservedLoadingRef = useRef(false);
    const scanHandlerRef = useRef<((scan: ScannerEvent) => void) | null>(null);
    const [selectedRowId, setSelectedRowId] = useState<Identifier | null>(null);
    const [selectedRowSnapshot, setSelectedRowSnapshot] = useState<ReceivingOrderRow | null>(null);
    const selectedRowRef = useRef<ReceivingOrderRow | null>(null);
    const [duplicateScan, setDuplicateScan] = useState<string | null>(null);
    const [quantityValue, setQuantityValue] = useState<number | ''>('');
    const quantityValueRef = useRef('');
    const [isQuantityScanMode, setIsQuantityScanMode] = useState(false);
    const isQuantityScanModeRef = useRef(false);
    const receivingConditionRef = useRef<ReceivingCondition>(2);
    const receiveRequestPendingRef = useRef(false);
    const hasRemainingQuantityRef = useRef(false);
    const receiveRowSignatureAtSubmitRef = useRef('');

    const updateQuantityValue = useCallback((value: number | '') => { quantityValueRef.current = value === '' ? '' : String(value); setQuantityValue(value); }, []);

    const updateQuantityScanMode = useCallback((active: boolean) => { isQuantityScanModeRef.current = active; setIsQuantityScanMode(active); }, []);

    const scanner = useScanner({ id: 'ReceivingProductScanner', onScan: scan => scanHandlerRef.current?.(scan), busy: formik.isSubmitting || duplicateScan != null || labelDialog != null, focus: { autoFocus: true, restore: true, restoreOn: [isQuantityScanMode, selectedRowId] }, input: { placeholder: isQuantityScanMode ? 'Scan quantity, Product ID, Manufacturer ID, EAN, OK or Cancel' : 'Scan Product ID, Manufacturer Product ID or EAN', ariaLabel: isQuantityScanMode ? 'Scan quantity, product ID, manufacturer product ID, EAN, OK or Cancel' : 'Scan product ID, manufacturer product ID or EAN' }, submitButton: { title: 'Submit' } });

    // Use the same API rows as the main table, independent of grid filtering.
    // Ignore rows retained by the request hook from another warehouse.
    const warehouseRows = useMemo(() => rows.filter(row => row.WarehouseId != null && String(row.WarehouseId) === String(warehouseId)), [rows, warehouseId]);

    const findMatches = useCallback((scan: string) => findScanMatches({ rows: warehouseRows, value: scan, getIdentifiers: row => [row.ProductId, row.ManufacturerProductId, row.EANCode] }), [warehouseRows]);

    const selectedRow: ReceivingOrderRow | null = warehouseRows.find(row => selectedRowId != null && String(row.ReceivingAdviceRowId) === String(selectedRowId)) ?? selectedRowSnapshot;
    selectedRowRef.current = selectedRow;
    const duplicateRows = useMemo(() => duplicateScan == null ? [] : findMatches(duplicateScan), [duplicateScan, findMatches]);

    const receiveRow = receiveResult?.rows?.[0] ?? null;
    const receiveRowSignature = receiveRow ? [receiveRow.DateReceived, receiveRow.ReceivingAdviceId, receiveRow.ProductId, receiveRow.QtyReceived, receiveRow.QtyRedundant, receiveRow.ReceivingConditionId].map((value) => String(value ?? '')).join('|') : '';
    const [lastReceiveRow, setLastReceiveRow] = useState<typeof receiveRow>(null);
    const receivedQuantity = lastReceiveRow?.QtyReceived ?? selectedRow?.ReceivedQty ?? selectedRow?.QuantityReceive ?? 0;
    const receivingStatus = getReceivingStatus(selectedRow?.QuantityExpected, receivedQuantity, lastReceiveRow?.QtyRedundant);
    const hasRemainingQuantity = selectedRow != null && receivingStatus.kind === 'remaining';
    hasRemainingQuantityRef.current = hasRemainingQuantity;

    const { resetForm, setSubmitting, setFieldValue } = formik;

    const submitReceive = useCallback(() => {
        const row = selectedRowRef.current;
        const latestQuantity = Number(quantityValueRef.current);
        const condition = receivingConditionRef.current;

        if (receiveRequestPendingRef.current || !isQuantityScanModeRef.current || !hasRemainingQuantityRef.current || !row || row.ProductId == null || row.ProductId === '' || row.StorageItemId == null || row.StorageItemId === '') {
            return false;
        }
        if (!Number.isFinite(latestQuantity) || latestQuantity <= 0 || ![1, 2].includes(condition)) {
            return false;
        }
        if (printProductLabelRef.current && !Number.isSafeInteger(latestQuantity)) {
            reportReceiveError('Enter a whole number of products when printing one label per product.');
            return false;
        }

        const barcode = barcodeValueRef.current.trim();
        const productId = Number(row.ProductId);
        if (barcode && (!Number.isSafeInteger(productId) || productId <= 0)) {
            reportReceiveError('A numeric product ID is required to save the barcode.');
            return false;
        }
        pendingProductBarcodeRef.current = barcode ? { productId, barcode } : null;

        // All scanner-sensitive values come from refs here. This path does not wait for
        // React state, a re-render, blur, or Formik validation before building the request.
        receiveRequestPendingRef.current = true;
        pendingProductLabelRef.current = printProductLabelRef.current ? { productId: row.ProductId, copies: latestQuantity, settings: printingSettings } : null;
        receiveRowAtSubmitRef.current = receiveRow;
        receiveStatusAtSubmitRef.current = receiveResult?.status;
        receiveObservedLoadingRef.current = false;
        setSubmitting(true);
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
        setReceivePayload({ receivingAdviceId: row.ReceivingAdviceId, productId: row.ProductId, storageItemId: row.StorageItemId, condition, warehouseId, quantityReceive: latestQuantity });
        return true;
    }, [reportReceiveError, printingSettings, receiveRow, receiveResult?.status, receiveRowSignature, setSubmitting, warehouseId]);

    useEffect(() => {
        if (!receiveRequestPendingRef.current) return;
        if (receiveResult?.loading) { receiveObservedLoadingRef.current = true; return; }
        const requestStatus = receiveResult?.status;
        const failed = requestStatus === 'error' || requestStatus === 'aborted' || (typeof requestStatus === 'number' && requestStatus >= 400);
        const freshStatus = receiveObservedLoadingRef.current || requestStatus !== receiveStatusAtSubmitRef.current;
        const succeeded = requestStatus === 'success' || (typeof requestStatus === 'number' && requestStatus >= 200 && requestStatus < 300);
        const matchesReceipt = receiveRow && String(receiveRow.ProductId) === String(receivePayload?.productId) && String(receiveRow.ReceivingAdviceId) === String(receivePayload?.receivingAdviceId);
        if (failed || (succeeded && !matchesReceipt)) {
            if (!freshStatus) return;
            pendingProductLabelRef.current = null;
            pendingProductBarcodeRef.current = null;
            receiveRequestPendingRef.current = false;
            setReceivePayload(null);
            setSubmitting(false);
            if (!failed) reportReceiveError('The receipt could not be confirmed. No labels were printed. Check the receiving order before trying again.');
            return;
        }
        if (!succeeded || !freshStatus) return;
        if (!receiveRow || (receiveRow === receiveRowAtSubmitRef.current && receiveRowSignature === receiveRowSignatureAtSubmitRef.current && !receiveObservedLoadingRef.current)) return;

        const productBarcode = pendingProductBarcodeRef.current;
        pendingProductBarcodeRef.current = null;
        if (productBarcode) onSaveProductBarcode(productBarcode);

        const productLabel = pendingProductLabelRef.current;
        pendingProductLabelRef.current = null;
        // The submitted quantity is this receipt's quantity, not the cumulative
        // QtyReceived total displayed by the receiving API.
        if (productLabel) onPrintProductLabel(productLabel);

        const quantityReceived = Number(receiveRow.QtyReceived);
        const receivingConditionId = Number(receiveRow.ReceivingConditionId);
        if (Number.isFinite(quantityReceived)) updateQuantityValue(quantityReceived);
        if (receivingConditionId === 1 || receivingConditionId === 2) {
            receivingConditionRef.current = receivingConditionId;
            setFieldValue('Condition', receivingConditionId, false);
        }
        setLastReceiveRow(receiveRow);
        hasRemainingQuantityRef.current = getReceivingStatus(selectedRowRef.current?.QuantityExpected, receiveRow.QtyReceived, receiveRow.QtyRedundant).kind === 'remaining';
        // Reset the request so another receipt can use the same entered quantity.
        setReceivePayload(null);
        scanner.clear();
        updateQuantityScanMode(false);
        receiveRequestPendingRef.current = false;
        setSubmitting(false);
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
        onReceived();
    }, [scanner.clear, reportReceiveError, receivePayload, receiveResult?.loading, receiveResult?.status, receiveRow, receiveRowSignature, onPrintProductLabel, onSaveProductBarcode, onReceived, setFieldValue, setSubmitting, updateQuantityScanMode, updateQuantityValue]);

    const quantityToReceive = Number(quantityValue);
    const hasReceivingIdentifiers = selectedRow != null && selectedRow.ProductId != null && selectedRow.ProductId !== '' && selectedRow.StorageItemId != null && selectedRow.StorageItemId !== '';
    const canEditQuantity = hasRemainingQuantity && !formik.isSubmitting;
    const canReceive = isQuantityScanMode && hasRemainingQuantity && hasReceivingIdentifiers && Number.isFinite(quantityToReceive) && quantityToReceive > 0 && (!printProductLabel || Number.isSafeInteger(quantityToReceive)) && [1, 2].includes(formik.values.Condition) && !formik.isSubmitting;
    const displayedStatus: ReceivingStatusValue = formik.isSubmitting ? { kind: 'receiving' } : receivingStatus;

    const clearReceivingForm = useCallback(() => {
        if (receiveRequestPendingRef.current) return;
        pendingProductLabelRef.current = null;
        pendingProductBarcodeRef.current = null;
        updateBarcodeValue('');
        const filterModel = tableSettings.filterModel ?? { items: [] };
        const items = filterModel.items.filter((item) => item.field !== 'ReceivingAdviceId');
        if (tableSettings.initialFilter?.field === 'ReceivingAdviceId') tableSettings.initialFilter = undefined;
        if (items.length !== filterModel.items.length) tableSettings.apiRef.current.setFilterModel({ ...filterModel, items });

        receivingConditionRef.current = 2;
        resetForm();
        setReceivePayload(null);
        scanner.clear();
        selectedRowRef.current = null;
        hasRemainingQuantityRef.current = false;
        setSelectedRowId(null);
        setSelectedRowSnapshot(null);
        setDuplicateScan(null);
        updateQuantityValue('');
        updateQuantityScanMode(false);
        setLastReceiveRow(null);
        receiveRequestPendingRef.current = false;
        receiveRowSignatureAtSubmitRef.current = receiveRowSignature;
    }, [scanner.clear, receiveRowSignature, resetForm, tableSettings, updateBarcodeValue, updateQuantityScanMode, updateQuantityValue]);

    const selectRow = useCallback((row: ReceivingOrderRow) => {
        if (receiveRequestPendingRef.current) return;
        if (!isReceivingFormVisible) onReceivingFormVisibilityChange(true);
        receivingConditionRef.current = 2;
        resetForm();
        selectedRowRef.current = row;
        const existingBarcode = row.EANCode ?? row.ManufacturerAltProductId;
        updateBarcodeValue(existingBarcode == null ? '' : String(existingBarcode));
        updateQuantityValue(1);
        setLastReceiveRow(null);
        setSelectedRowId(row.ReceivingAdviceRowId);
        setSelectedRowSnapshot(row);
        setDuplicateScan(null);
        const hasRemaining = getReceivingStatus(row.QuantityExpected, row.ReceivedQty ?? row.QuantityReceive ?? 0).kind === 'remaining';
        hasRemainingQuantityRef.current = hasRemaining;
        updateQuantityScanMode(hasRemaining);
    }, [isReceivingFormVisible, onReceivingFormVisibilityChange, resetForm, updateBarcodeValue, updateQuantityScanMode, updateQuantityValue]);

    const handleScan = ({ value: scan, command, ean }: ScannerEvent) => {
        if (receiveRequestPendingRef.current || labelDialog != null || duplicateScan != null) return;

        // Scanning reveals the form and persists the same preference as the menu.
        if (!isReceivingFormVisible) onReceivingFormVisibilityChange(true);

        // Resolve all three identifiers before commands, quantities, or assigning a
        // new EAN. String comparison preserves leading zeroes and keeps every row.
        const activeRow = selectedRowRef.current;
        const matches = findMatches(scan);
        if (matches.length === 1 && activeRow && String(matches[0].ReceivingAdviceRowId) === String(activeRow.ReceivingAdviceRowId) && isQuantityScanModeRef.current) {
            const currentQuantity = Number(quantityValueRef.current);
            const nextQuantity = Number.isFinite(currentQuantity) ? currentQuantity + 1 : 1;
            updateQuantityValue(nextQuantity);
            return;
        }

        if (matches.length === 0) {
            // Only an unmatched valid EAN can be assigned to the active product.
            if (activeRow && ean?.valid) {
                updateBarcodeValue(scan);
                return;
            }

            if (command === 'cancel') {
                clearReceivingForm();
                return;
            }
            if (command === 'ok') {
                submitReceive();
                return;
            }

            if (isQuantityScanModeRef.current) {
                const quantity = Number(scan);
                if (Number.isFinite(quantity) && quantity >= 0 && quantity <= 1000) {
                    updateQuantityValue(quantity);
                    scanner.focus();
                    return;
                }

                // Numeric scans above 1000 continue to the unmatched-identifier error.
                if (!Number.isFinite(quantity) || quantity <= 1000) {
                    scanner.focus();
                    return;
                }
            }

            // An unrecognized scan never discards the loaded form.
            const invalidEan = activeRow && ean != null && !ean.valid;
            reportReceiveError(invalidEan ? `Scanned value "${scan}" has an invalid EAN check digit. The loaded product has been kept.` : `Scanned value "${scan}" did not match a Product ID, Manufacturer Product ID or EAN${activeRow ? ', or a valid new EAN-8/EAN-13 barcode' : ''}. The form has been kept.`);
            scanner.focus();
            return;
        }

        // Clear the previous display only after a product match is found.
        updateBarcodeValue('');
        receivingConditionRef.current = 2;
        resetForm();
        setReceivePayload(null);
        updateQuantityValue('');
        updateQuantityScanMode(false);
        setLastReceiveRow(null);
        selectedRowRef.current = null;
        setSelectedRowId(null);
        setSelectedRowSnapshot(null);
        setDuplicateScan(null);

        if (matches.length === 1) {
            selectRow(matches[0]);
        } else {
            setDuplicateScan(scan);
        }
    };

    scanHandlerRef.current = handleScan;

    const openProductLabelDialog = () => {
        const productId = selectedRow?.ProductId;
        if (productId == null || productId === '' || receiveRequestPendingRef.current) return;
        reprintSubmittedRef.current = false;
        setLabelDialog({ productId, copies: String(receivedQuantity ?? 0) });
    };
    const labelCopies = Number(labelDialog?.copies);
    const validLabelCopies = labelDialog?.copies.trim() !== '' && Number.isSafeInteger(labelCopies) && labelCopies > 0;
    const submitProductLabel = () => {
        if (!labelDialog || !validLabelCopies || reprintSubmittedRef.current) return;
        reprintSubmittedRef.current = true;
        onPrintProductLabel({ productId: labelDialog.productId, copies: labelCopies, settings: printingSettings });
        setLabelDialog(null);
    };
    const receivingOrderId = lastReceiveRow?.ReceivingAdviceId ?? selectedRow?.ReceivingAdviceId;
    const hasReceivingOrderId = receivingOrderId != null && receivingOrderId !== '';
    const currentFilterModel = tableSettings.filterModel ?? { items: [] };
    const isReceivingOrderFilter = (item: (typeof currentFilterModel.items)[number]) => item.field === 'ReceivingAdviceId' && item.operator === 'equals' && String(item.value) === String(receivingOrderId);
    const isReceivingOrderFiltered = hasReceivingOrderId && currentFilterModel.items.some(isReceivingOrderFilter);
    const filterReceivingOrder = () => {
        if (!hasReceivingOrderId) return;
        if (isReceivingOrderFiltered) {
            tableSettings.initialFilter = undefined;
            tableSettings.apiRef.current.setFilterModel({ ...currentFilterModel, items: currentFilterModel.items.filter((item) => !isReceivingOrderFilter(item)) });
            return;
        }
        tableSettings.initialFilter = { field: 'ReceivingAdviceId', operator: 'equals', value: String(receivingOrderId) };
        // Apply to the mounted grid too, since its filterModel is controlled.
        tableSettings.apiRef.current.setFilterModel({ items: [tableSettings.initialFilter], quickFilterValues: [] });
    };

    const quantityError = quantityValue !== '' && (!Number.isFinite(quantityToReceive) || quantityToReceive <= 0) ? 'Enter a quantity greater than 0.' : printProductLabel && quantityValue !== '' && !Number.isSafeInteger(quantityToReceive) ? 'Use a whole number when printing one label per product.' : undefined;
    const rowFormProps = { ...formik, values: { ...formik.values, ReceivingAdviceId: receivingOrderId ?? '', ReceivingAdviceRowId: selectedRow?.ReceivingAdviceRowId ?? '', ReceivingAdviceType: receivingAdviceTypes[String(selectedRow?.ReceivingAdviceTypeId)] ?? String(selectedRow?.ReceivingAdviceTypeId ?? ''), Sender: selectedRow?.Sender ?? '', ProductName: lastReceiveRow?.ProductName ?? selectedRow?.ProductName ?? '', ProductId: lastReceiveRow?.ProductId ?? selectedRow?.ProductId ?? '', ManufacturerProductId: selectedRow?.ManufacturerProductId ?? '', QuantityExpected: selectedRow?.QuantityExpected ?? '', ReceivedQuantity: receivedQuantity, PrintProductLabel: printProductLabel, Barcode: barcodeValue, Quantity: quantityValue }, errors: { ...formik.errors, Quantity: quantityError }, touched: { ...formik.touched, Quantity: true }, handleChange: (event) => {
            const { name, value, checked } = event.target;
            if (name === 'Quantity') { updateQuantityValue(value === '' ? '' : Number(value)); updateQuantityScanMode(hasRemainingQuantity); }
            else if (name === 'Barcode') updateBarcodeValue(value);
            else if (name === 'Condition') { receivingConditionRef.current = Number(value) as ReceivingCondition; setFieldValue(name, Number(value)); }
            else if (name === 'PrintProductLabel') { printProductLabelRef.current = checked; setSetting('receivingOrdersPrintProductLabel', checked); }
        } };

    const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement) || event.nativeEvent.isComposing) return;
        const name = event.target.name;
        if (name !== 'Quantity' && name !== 'Barcode') return;
        event.preventDefault();
        event.stopPropagation();
        if (name === 'Barcode') { updateBarcodeValue(event.target.value); scanner.focus(); }
        else submitReceive();
    };
    const closeDuplicateScan = () => { setDuplicateScan(null); scanner.focus(); };
    const changeWarehouse = (value: Identifier) => { if (!receiveRequestPendingRef.current) onWarehouseChange(value); };
    return { scanner, canReceive, canEditQuantity, submitReceive, displayedStatus, selectedRow, selectRow, rowFormProps, isReceivingOrderFiltered, hasReceivingOrderId, receivingOrderId, filterReceivingOrder, clearReceivingForm, openProductLabelDialog, onKeyDown, duplicateScan, duplicateRows, closeDuplicateScan, labelDialog, setLabelDialog, validLabelCopies, submitProductLabel, changeWarehouse };
};

export const receivingConditions = { 1: 'Broken', 2: 'OK' } as const;
type ReceivingCondition = keyof typeof receivingConditions;
type ReceivePayload = NonNullable<Parameters<typeof useReceiveProducts>[0]>;

export type ReceiveValues = { Condition: ReceivingCondition };

export const receiveSchema = Yup.object({ Condition: Yup.number().oneOf([1, 2], 'Select a receiving condition').required('Condition is required') });

type ReceivingStatusValue = { kind: 'remaining' | 'surplus' | 'done' | 'receiving' | 'pending'; quantity?: number };

const getReceivingStatus = (expectedValue: unknown, receivedValue: unknown, redundantValue: unknown = 0): ReceivingStatusValue => {
    if (expectedValue == null || expectedValue === '' || receivedValue == null || receivedValue === '') return { kind: 'pending' };
    const expected = Number(expectedValue);
    const received = Number(receivedValue);
    const redundant = Number(redundantValue);

    if (Number.isFinite(redundant) && redundant > 0) return { kind: 'surplus', quantity: redundant };
    if (!Number.isFinite(expected) || !Number.isFinite(received)) return { kind: 'pending' };
    if (received > expected) return { kind: 'surplus', quantity: received - expected };
    if (received === expected) return { kind: 'done' };
    return { kind: 'remaining', quantity: expected - received };
};

type ReceivingStationOptions = { warehouseId: Identifier | null; onWarehouseChange: (value: Identifier) => void; isReceivingFormVisible: boolean; onReceivingFormVisibilityChange: (visible: boolean) => void; onPrintProductLabel: (request: ProductLabelRequest) => void; onSaveProductBarcode: (request: ProductBarcodeRequest) => void; tableData: ReceivingOrdersTableData; tableSettings: tableSettingsProps; onReceived: () => void; formik: FormikProps<ReceiveValues> };

export type ReceivingStation = ReturnType<typeof useReceivingStation>;

export const ReceivingEditor = ({ station, warehouseId }: { station: ReceivingStation; warehouseId: Identifier }) => {
    const { selectedRow, rowFormProps, canEditQuantity, displayedStatus, hasReceivingOrderId, receivingOrderId, isReceivingOrderFiltered, filterReceivingOrder, clearReceivingForm, openProductLabelDialog } = station;
    const receiveAction = <ReceivingAction station={station} />;
    return <WarehouseRowEditor config={{
        id: 'Receiving editor', label: 'Receive products', disabled: station.rowFormProps.isSubmitting, onSubmit: () => { station.submitReceive(); }, submitOnEnter: ['Quantity'],
        summaries: [
            { id: 'order', label: 'Receiving order', title: hasReceivingOrderId ? `Receiving order ${receivingOrderId}` : 'Select a receiving order', details: [
                    { id: 'ReceivingAdviceRowId', label: 'Row ID', value: selectedRow?.ReceivingAdviceRowId ?? '' }, { id: 'ReceivingAdviceType', label: 'Type', value: rowFormProps.values.ReceivingAdviceType }, { id: 'Sender', label: 'Sender', value: selectedRow?.Sender ?? '' },
                ], actions: [
                    <CustomButton key="view-order" variant="outlined" size="small" title="View order" tooltipText="View receiving order" icon={<VisibilityIcon />} disabled={!hasReceivingOrderId} actionUrl={`/warehouse/receiving-orders/edit?id=${encodeURIComponent(String(receivingOrderId))}&warehouseId=${encodeURIComponent(String(warehouseId))}`} target="_blank" />,
                    <CustomButton key="filter" variant={isReceivingOrderFiltered ? 'contained' : 'outlined'} size="small" title={isReceivingOrderFiltered ? 'Remove order filter' : 'Filter order'} tooltipText={isReceivingOrderFiltered ? 'Remove order filter' : 'Filter receiving order'} icon={<FilterAltIcon />} disabled={!hasReceivingOrderId} onClick={filterReceivingOrder} />,
                    <CustomButton key="clear" variant="text" size="small" title="Clear" tooltipText="Clear receiving form" icon={<ClearIcon />} disabled={station.rowFormProps.isSubmitting} onClick={clearReceivingForm} />,
                ] },
            { id: 'product', label: 'Receiving product', title: rowFormProps.values.ProductName || 'Select a product', details: [
                    { id: 'ProductId', label: 'Product ID', value: rowFormProps.values.ProductId }, { id: 'ManufacturerProductId', label: 'Manufacturer ID', value: rowFormProps.values.ManufacturerProductId }, { id: 'ReceivedQuantity', label: 'Received', value: rowFormProps.values.ReceivedQuantity },
                ] },
        ],
        actions: [<CustomButton key="product-label" iconButton title="Product Label" tooltipText="Print labels for this product" size="small" icon={<PrintIcon />} disabled={station.rowFormProps.isSubmitting || !selectedRow || selectedRow.ProductId == null || selectedRow.ProductId === ''} onClick={openProductLabelDialog} />, <ReceivingStatus key="status" status={displayedStatus} hasSelectedRow={selectedRow != null} />, receiveAction],
        layout: { columns: 'minmax(180px, 1.5fr) minmax(120px, 1fr) 100px minmax(120px, 1fr) max-content', areas: '"barcode condition expected quantity print"', compactColumns: 'minmax(140px, 1fr) 100px minmax(120px, 1fr) max-content', compactAreas: '"barcode barcode barcode print" "condition expected quantity print"', minWidth: 620 },
        fields: [
            { id: 'barcode', content: <FormField fieldProps={{ name: 'Barcode', label: 'Barcode(EAN)', disabled: !selectedRow || station.rowFormProps.isSubmitting }} formikProps={rowFormProps} /> },
            { id: 'condition', content: <FormField fieldProps={{ name: 'Condition', label: 'Condition', select: true, disabled: !canEditQuantity }} formikProps={rowFormProps}>{Object.entries(receivingConditions).map(([id, label]) => <MenuItem key={id} value={Number(id)}>{label}</MenuItem>)}</FormField> },
            { id: 'expected', content: <FormField fieldProps={{ name: 'QuantityExpected', label: 'Expected', disabled: true }} formikProps={rowFormProps} /> },
            { id: 'quantity', content: <FormField fieldProps={{ name: 'Quantity', label: 'To receive', type: 'number', disabled: !canEditQuantity }} formikProps={rowFormProps} /> },
            { id: 'print', content: <FormField fieldProps={{ name: 'PrintProductLabel', label: 'Print product label', checkbox: true, disabled: station.rowFormProps.isSubmitting }} formikProps={rowFormProps} /> },
        ],
    }} />;
};

export const ReceivingAction = ({ station }: { station: ReceivingStation }) => <CustomButton title="Receive" icon={<ArrowUpwardIcon />} isSubmitting={station.rowFormProps.isSubmitting} disabled={!station.canReceive} onClick={station.submitReceive} />;

export const ReceivingDialogs = ({ station, status }: { station: ReceivingStation; status: ReceivingOrdersTableData['status'] }) => {
    const { duplicateScan, duplicateRows, closeDuplicateScan, labelDialog, setLabelDialog, validLabelCopies, submitProductLabel } = station;
    return <>
        {duplicateScan != null && <ReceivingRowPicker scan={duplicateScan} rows={duplicateRows} status={status} onSelect={row => { station.selectRow(row); }} onClose={closeDuplicateScan} />}
        {labelDialog && <Dialog open onClose={() => setLabelDialog(null)} fullWidth maxWidth="xs" aria-labelledby="product-label-dialog-title">
            <Box component="form" onSubmit={(event) => { event.preventDefault(); submitProductLabel(); }} noValidate>
                <DialogTitle id="product-label-dialog-title">Print product label</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>Product {labelDialog.productId}</Typography>
                    <CustomField options={{ name: 'LabelCopies', label: 'Number of labels', type: 'number' }} stateProps={{ values: { LabelCopies: labelDialog.copies }, errors: { LabelCopies: !validLabelCopies ? 'Enter a positive whole number.' : undefined }, touched: { LabelCopies: true }, handleChange: event => setLabelDialog({ ...labelDialog, copies: event.target.value }) }} />
                </DialogContent>
                <DialogActions><CustomButton title="Cancel" variant="text" onClick={() => setLabelDialog(null)} /><CustomButton title="Print" icon={<PrintIcon />} disabled={!validLabelCopies} onClick={submitProductLabel} /></DialogActions>
            </Box>
        </Dialog>}
    </>;
};

const ReceivingStatus = ({ status, hasSelectedRow }: { status: ReceivingStation['displayedStatus']; hasSelectedRow: boolean }) => {
    const tone = status.kind === 'done' ? 'success' : status.kind === 'surplus' ? 'warning' : 'info';
    const isPending = status.kind === 'pending';
    const label = status.kind === 'done' ? 'Done' : status.kind === 'surplus' ? 'surplus received' : status.kind === 'remaining' ? 'left to receive' : status.kind === 'receiving' ? 'Receiving...' : hasSelectedRow ? 'Awaiting quantities' : 'Select a receiving row';
    const StatusIcon = status.kind === 'done' ? CheckCircleOutlineIcon : status.kind === 'surplus' ? WarningAmberRoundedIcon : status.kind === 'remaining' ? Inventory2OutlinedIcon : InfoOutlinedIcon;

    return (
        <Box component="fieldset" sx={{ m: 0, px: 1, pb: 0.5, pt: 0, minWidth: 0, width: 'fit-content', maxWidth: '100%', border: '1px solid', borderRadius: 1, borderColor: isPending ? 'divider' : `${tone}.main`, bgcolor: (theme) => isPending ? 'transparent' : alpha(theme.palette[tone].main, 0.06) }}>
            <Typography component="legend" sx={{ px: 0.5, fontSize: '0.65rem', lineHeight: 1.2, color: isPending ? 'text.secondary' : `${tone}.main` }}>Receiving status</Typography>
            <Box role="status" aria-live="polite" aria-atomic="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 22, color: isPending ? 'text.secondary' : `${tone}.main` }}>
                {status.kind === 'receiving' ? <CircularProgress size={18} color="inherit" aria-label="Receiving products" /> : <StatusIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.5 }}>
                    {status.quantity != null && <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{status.quantity.toLocaleString(undefined, { maximumFractionDigits: 20 })}</Typography>}
                    <Typography component="span" sx={{ fontSize: '0.8rem', lineHeight: 1.3, fontWeight: isPending ? 400 : 600 }}>{label}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

const ReceivingRowPicker = ({ scan, rows, status, onSelect, onClose }: { scan: string; rows: ReceivingOrderRow[]; status: ReceivingOrdersTableData['status']; onSelect: (row: ReceivingOrderRow) => void; onClose: () => void }) => {
    const [columns, tableSettings] = useReceivingOrderMatchesTable(rows, onSelect, status);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl" aria-labelledby="receiving-row-picker-title">
            <DialogTitle id="receiving-row-picker-title">Choose a receiving order row</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>Multiple rows match "{scan}". Check the order and sender, then choose a row.</Typography>
                <CustomDataGrid columns={columns} tableSettings={tableSettings} />
            </DialogContent>
            <DialogActions><CustomButton title="Cancel" variant="text" onClick={onClose} /></DialogActions>
        </Dialog>
    );
};

export const useReceivingFollowUps = () => {
    // Keep the print queue above the warehouse-keyed station so changing the
    // warehouse or clearing the form cannot drop an already accepted print job.
    const [labelJobs, setLabelJobs] = useState<ProductLabelJob[]>([]);
    const { updateMessageStatus } = useMessageContext();
    const labelSequence = useRef(0);
    const enqueueProductLabel = useCallback((request: ProductLabelRequest) => {
        const job = { ...request, key: ++labelSequence.current };
        setLabelJobs((current) => [...current, job]);
    }, []);
    const labelJob = labelJobs[0];
    const [barcodeJobs, setBarcodeJobs] = useState<ProductBarcodeJob[]>([]);
    const [barcodeUpdateFailed, setBarcodeUpdateFailed] = useState(false);
    const [barcodeAttempt, setBarcodeAttempt] = useState(0);
    const barcodeSequence = useRef(0);
    const enqueueProductBarcode = useCallback((request: ProductBarcodeRequest) => {
        const job = { ...request, key: ++barcodeSequence.current };
        setBarcodeJobs((current) => [...current, job]);
    }, []);
    const barcodeJob = barcodeJobs[0];
    const completeProductBarcode = useCallback((failed: boolean) => {
        if (failed) { setBarcodeUpdateFailed(true); return; }
        setBarcodeJobs((current) => current.slice(1));
    }, []);
    const completeProductLabel = useCallback((result: ProductLabelPrintResult) => {
        if (!labelJob) return;
        const prefix = `Product ${labelJob.productId} (${labelJob.copies} labels): `;
        const outcome = result.outcome;
        const message = result.error ? `${prefix}${result.error} Use Product Label to retry printing.` : outcome?.method === 'printer' ? `${prefix}Sent to "${outcome.printer}".` : `${prefix}${outcome?.method === 'savePdf' ? `PDF downloaded. Print ${labelJob.copies} copies from the downloaded label.` : 'Printing skipped.'}${outcome?.reason ? ` ${outcome.reason}` : ''}`;
        updateMessageStatus({ requestUrl: printProductLabel(labelJob.productId).url, status: result.error || outcome?.method === 'skip' ? 'error' : 'success', message });
        setLabelJobs((current) => current.filter((job) => job.key !== labelJob.key));
    }, [labelJob, updateMessageStatus]);
    const feedback = barcodeJob ? <Box role="status" sx={{ mb: 2 }}>
        <Typography>{barcodeUpdateFailed ? `Receipt saved. Barcode ${barcodeJob.barcode} for product ${barcodeJob.productId} is waiting for retry. Further barcode saves are queued.` : `Saving barcode for product ${barcodeJob.productId}…`}</Typography>
        {barcodeUpdateFailed && <CustomButton title="Retry barcode" variant="outlined" size="small" onClick={() => { setBarcodeAttempt(current => current + 1); setBarcodeUpdateFailed(false); }} />}
    </Box> : null;

    const jobs = <>
        {barcodeJob && !barcodeUpdateFailed && <ProductBarcodeUpdate key={`product-barcode-${barcodeJob.key}-${barcodeAttempt}`} job={barcodeJob} onComplete={completeProductBarcode} />}
        {labelJob && <ProductLabelPrintJob key={`product-label-${labelJob.key}`} productId={labelJob.productId} copies={labelJob.copies} settings={labelJob.settings} onComplete={completeProductLabel} />}
    </>;
    return { jobs, feedback, enqueueProductLabel, enqueueProductBarcode };
};

// Mount only for a confirmed receipt. A fresh keyed instance also supports
// successive updates to the same product with the hook's id-only dependency.
const ProductBarcodeUpdate = ({ job, onComplete }: { job: ProductBarcodeJob; onComplete: (failed: boolean) => void }) => {
    // The update endpoint accepts this partial body; its shared create type is broader.
    const body = useMemo(() => ({ ManufacturerAltProductId: job.barcode } as Parameters<typeof useUpdateProduct>[1]), [job.barcode]);
    const [, , , loading, status] = useUpdateProduct(job.productId, body);
    const completedRef = useRef(false);
    useEffect(() => {
        if (completedRef.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || (typeof status === 'number' && status >= 400);
        const succeeded = status === 'success' || (typeof status === 'number' && status >= 200 && status < 300);
        if (!failed && !succeeded) return;
        completedRef.current = true;
        onComplete(failed);
    }, [loading, status, onComplete]);
    return null;
};

export type Identifier = string | number;
export type ProductLabelRequest = { productId: Identifier; copies: number; settings: DispatchPrintingSettings };
type ProductLabelJob = ProductLabelRequest & { key: number };
export type ProductBarcodeRequest = { productId: number; barcode: string };
type ProductBarcodeJob = ProductBarcodeRequest & { key: number };
